import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  CtaBand,
  Eyebrow,
  Lead,
  PrimaryLink,
  Quote,
  TextLink,
} from "@/components/site/primitives";
import { SITE, TESTIMONIALS } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { PartOfHub, RelatedLinks } from "@/components/site/RelatedLinks";
import { breadcrumbMeta } from "@/lib/internal-links";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { contactFunnel, trackOutboundClick } from "@/lib/analytics";
import { useSectionViews, type TrackedSection } from "@/hooks/useSectionViews";
import { AREAS, jsonLdScript, professionalServiceSchema, serviceSchema } from "@/lib/schema";

const PATH = "/fractional-cto-manchester";
const CTA_SOURCE_HERO = "fractional_cto_manchester_hero";
const CTA_SOURCE_BAND = "fractional_cto_manchester_footer";

/** Sections reported as read, in page order, keyed by their heading id. */
const TRACKED_SECTIONS: TrackedSection[] = [
  { id: "fctomcr-hero-heading", name: "Hero" },
  { id: "fctomcr-who-heading", name: "Who I work with" },
  { id: "fctomcr-when-heading", name: "When founders bring me in" },
  { id: "fctomcr-cadence-heading", name: "Cadence" },
  { id: "fctomcr-sectors-heading", name: "Sectors" },
  { id: "fctomcr-quote-heading", name: "Testimonial" },
  { id: "fctomcr-faq-heading", name: "FAQ" },
];

const title = "Fractional CTO in Manchester | Timothy Ng, Scryla";
const description =
  "Missed deadlines or an underperforming dev team? Manchester-based fractional CTO: technology audits, delivery assurance and hiring, for founders and boards.";

const SECTORS = ["Fintech", "iGaming and gambling", "Digital health", "SaaS", "Data", "Agencies"];

const WHEN = [
  {
    title: "You are competing for engineers against much bigger payrolls",
    body: "Manchester has a deep technical talent pool and no shortage of employers bidding for it. A fractional CTO lets you buy the senior judgement first and hire the team around a plan, rather than paying up for a leader before you know what shape the role should be.",
  },
  {
    title: "A local agency built it and now nobody owns it",
    body: "The North West is thick with development agencies, and plenty of good products started in one. Someone independent has to read the codebase, the contract and the roadmap before the handover, renewal or rebuild decision lands.",
  },
  {
    title: "You want the CTO in the room, not on a call",
    body: "Board meetings, delivery reviews and difficult conversations with a supplier go better face to face. Being based here means on-site days are a short journey rather than a line item.",
  },
  {
    title: "Northern investors are about to look at the technology",
    body: "Regional funds and angel syndicates diligence the build the same way anyone else does. Knowing what they will find, and having the answers written down before they ask, is worth more than a tidy deck.",
  },
];

const CADENCE = [
  {
    title: "On-site across Greater Manchester",
    body: "City centre, Salford, Stockport, Trafford and the surrounding boroughs are a short journey, so in-person days with founders and development teams are straightforward to arrange rather than something to negotiate.",
  },
  {
    title: "The North West corridor",
    body: "Leeds and Liverpool are both inside an hour by train. Where a client wants regular face-to-face time on a delivery review or a board meeting, that stays practical without travel dominating the engagement.",
  },
  {
    title: "Remote for everything in between",
    body: "Written reports, scheduled calls and availability between them. Most of the work runs this way already, with in-person time used where it genuinely changes the outcome.",
  },
];

const FAQ = [
  {
    q: "Are you actually based in Manchester?",
    a: "Yes. Manchester is where I am based and where Scryla, the consultancy I founded in 2014, has operated from. It is not a service-area page for a business located somewhere else.",
  },
  {
    q: "Will you work on-site with my team in Manchester?",
    a: "Yes. Being local means on-site days are easy to arrange, sitting with a development team, running a delivery review in person, or attending a board meeting. The default rhythm is remote-first with in-person time where it earns its place, and in Manchester that time costs you nothing in travel.",
  },
  {
    q: "Do you only work with Manchester companies?",
    a: "No. Manchester and the North West are where in-person work is easiest, but I work with founders across the UK and internationally on a remote-first basis. Manchester to London is roughly two hours by train, so a monthly on-site rhythm in the South East is practical too.",
  },
  {
    q: "What does a fractional CTO actually do?",
    a: "Audits where your technology, team, spend and risk stand today; sets a technology plan that follows from the business plan, with sequence and cost named; holds in-house and outsourced development teams to what they promised; and coaches founders and technical leads so the capability stays in the business.",
  },
  {
    q: "How much does a fractional CTO cost in Manchester?",
    a: "Quoted per engagement rather than advertised. What moves the number is how often you need me and what I am accountable for: a delivery review with a written report sits at one end, a standing seat through a rebuild at the other. On-site days in Manchester and the North West carry no travel cost, which is not true of every arrangement you will be quoted.",
  },
  {
    q: "Is this the same as a technology non-executive director?",
    a: "No, and I keep the two separate. A fractional CTO works inside the business, owning technical decisions and leading the team. A non-executive director sits outside it, holding the executive to account and giving the board independent assurance. I will say which one you actually need.",
  },
];

export const Route = createFileRoute("/fractional-cto-manchester")({
  // Static marketing content: let the CDN serve it and revalidate in the background.
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta(
        "fractional-cto-manchester",
        "Fractional CTO in Manchester and the North West | Timothy Ng",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      jsonLdScript(
        professionalServiceSchema({
          path: PATH,
          name: "Fractional CTO in Manchester | Timothy Ng, Scryla",
          areaServed: AREAS.manchester,
          serviceType: "Fractional CTO",
        }),
      ),
      jsonLdScript(
        serviceSchema({
          path: PATH,
          serviceType: "Fractional CTO",
          areaServed: AREAS.manchester,
        }),
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      breadcrumbMeta(PATH),
    ],
  }),
  component: FractionalCtoManchesterPage,
});

function FractionalCtoManchesterPage() {
  useSectionViews(PATH, TRACKED_SECTIONS);

  return (
    <div>
      <section
        aria-labelledby="fctomcr-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Breadcrumbs path={PATH} className="mb-6" />
          <Eyebrow>Fractional CTO · Manchester</Eyebrow>
          <h1
            id="fctomcr-hero-heading"
            className="mt-4 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Fractional CTO in Manchester.
          </h1>
          <Lead className="mt-6">
            Delivery dates moving and a dev team you cannot properly assess? Senior technical
            leadership for founders and boards in Manchester and the North West, without a full-time
            executive hire. Former CTO and COO with twenty years in technology leadership, running
            my own consultancy for over a decade.
          </Lead>
          <PartOfHub hub="/fractional-cto-uk" className="mt-5" />
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Arrange a conversation
            </PrimaryLink>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick("linkedin", CTA_SOURCE_HERO)}
              className="inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background px-6 py-3.5 text-[15px] font-semibold transition-colors hover:border-primary hover:text-primary"
            >
              View my LinkedIn profile
            </a>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctomcr-who-heading">
        <Container>
          <h2
            id="fctomcr-who-heading"
            className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Who you would be working with
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body md:text-[19px]">
            <p>
              I am Timothy Ng. I founded Scryla in Manchester in 2014 and have held CTO and COO
              roles in technology businesses, so the judgement you are buying comes from running
              technology and operations, not from advising on them.
            </p>
            <p>
              I take on a small number of clients at a time, and I have no development team to keep
              busy and no supplier referrals to protect. If the work is not right for me, I will say
              so.
            </p>
          </div>
          <RelatedLinks className="mt-7" paths={["/who-i-am", "/what-i-do"]} />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fctomcr-when-heading">
        <Container>
          <Eyebrow>When to hire one</Eyebrow>
          <h2
            id="fctomcr-when-heading"
            className="mt-3 max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            The moments founders call me
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {WHEN.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctomcr-cadence-heading">
        <Container>
          <Eyebrow>How the work runs</Eyebrow>
          <h2
            id="fctomcr-cadence-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            On-site where it helps, remote where it does not
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-3">
            {CADENCE.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
          <p className="mt-7 text-[15px] text-body">{SITE.officeHours}</p>
        </Container>
      </section>

      <section
        className="border-b border-border bg-surface"
        aria-labelledby="fctomcr-sectors-heading"
      >
        <Container>
          <h2
            id="fctomcr-sectors-heading"
            className="max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Sectors I have run technology in
          </h2>
          <ul className="mt-7 flex flex-wrap gap-2.5">
            {SECTORS.map((sector) => (
              <li
                key={sector}
                className="rounded-full border border-border bg-card px-4 py-2 text-[15px] text-body"
              >
                {sector}
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctomcr-quote-heading">
        <Container>
          <h2 id="fctomcr-quote-heading" className="sr-only">
            What clients say
          </h2>
          <div className="max-w-[70ch]">
            <Quote item={TESTIMONIALS.aaronBiggs} />
          </div>
          <RelatedLinks className="mt-7" paths={["/happy-clients"]} />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fctomcr-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="fctomcr-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Fractional CTO in Manchester: FAQs
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
          <RelatedLinks
            className="mt-7"
            paths={[
              "/fractional-cto-uk",
              "/fractional-cto-vs-full-time-vs-agency",
              "/board-advisory",
            ]}
          />
        </Container>
      </section>

      <CtaBand
        heading="Need a fractional CTO in Manchester?"
        body="Tell me about the business, the stage it is at, and what is worrying you. I will tell you plainly whether I am the right person, and what I would look at first."
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
